import { useMemo, ReactNode, useState } from 'react';
import { useAsync } from 'react-use';
import { Progress } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import { TemplateEntityV1beta3 } from '@backstage/plugin-scaffolder-common';
import {
  Container,
  Flex,
  Text,
  Grid,
  SearchField,
  ToggleButtonGroup,
  ToggleButton,
} from '@backstage/ui';
import { CustomTemplateCard } from './custom-template-card';
import { useTemplateCategories } from './use-templates-categories';

type TemplateGroup = {
  title: string;
  description?: ReactNode;
  filter: (entity: Entity) => boolean;
};

type ScaffolderPageProps = {
  category?: string;
  groups?: TemplateGroup[];
};

type GroupedTemplate = {
  title: string;
  description?: ReactNode;
  templates: Entity[];
};

const getEntityRef = (entity: Entity): string => {
  const namespace = entity.metadata.namespace ?? 'default';
  return `${entity.kind}:${namespace}/${entity.metadata.name}`.toLowerCase();
};

const getTemplateType = (entity: Entity): string | undefined => {
  const template = entity as TemplateEntityV1beta3;
  return template.spec?.type;
};

const sortTemplatesByStarred = (
  entities: Entity[],
  starredEntityRefs: Set<string>,
): Entity[] => {
  return [...entities].sort((a, b) => {
    const aRef = getEntityRef(a);
    const bRef = getEntityRef(b);
    const aStarred = starredEntityRefs.has(aRef);
    const bStarred = starredEntityRefs.has(bRef);

    // Starred templates come first
    if (aStarred !== bStarred) {
      return aStarred ? -1 : 1;
    }

    // Then sort alphabetically by title
    const titleA = (a.metadata.title ?? a.metadata.name).toLowerCase();
    const titleB = (b.metadata.title ?? b.metadata.name).toLowerCase();
    return titleA.localeCompare(titleB);
  });
};

const filterTemplatesByCategory = (
  entities: Entity[],
  category?: string,
): Entity[] => {
  const isNotExperimental = (entity: Entity) =>
    entity?.spec?.lifecycle !== 'experimental';

  if (!category) {
    return entities.filter(isNotExperimental);
  }

  return entities.filter(
    entity => isNotExperimental(entity) && getTemplateType(entity) === category,
  );
};

const findUnmatchedTemplates = (
  templates: Entity[],
  groupedTemplates: GroupedTemplate[],
): Entity[] => {
  const matchedTemplates = new Set<Entity>();
  for (const group of groupedTemplates) {
    for (const template of group.templates) {
      matchedTemplates.add(template);
    }
  }

  return templates.filter(template => !matchedTemplates.has(template));
};

const getSearchableFields = (entity: Entity): string[] => {
  const template = entity as TemplateEntityV1beta3;
  const title = entity.metadata.title ?? entity.metadata.name;
  const description = entity.metadata.description ?? '';
  const owner = template.spec?.owner ?? '';
  return [title, description, owner];
};

const filterTemplatesBySearch = (
  entities: Entity[],
  searchText: string,
): Entity[] => {
  const needle = searchText.trim().toLowerCase();

  if (!needle) {
    return entities;
  }

  return entities.filter(entity =>
    getSearchableFields(entity).some(field =>
      field.toLowerCase().includes(needle),
    ),
  );
};

const createFallbackGroupTitle = (
  hasGroupedTemplates: boolean,
  category?: string,
): string => {
  const categoryLabel = category
    ? `${category.charAt(0).toUpperCase() + category.slice(1)}`
    : '';

  const titleText = hasGroupedTemplates
    ? `Other ${categoryLabel} Templates`
    : `Choose from${categoryLabel} Templates`;

  return titleText;
};

const useTemplateGroups = (
  templates: Entity[],
  starredEntityRefs: Set<string>,
  groups?: TemplateGroup[],
  category?: string,
): GroupedTemplate[] => {
  return useMemo(() => {
    const categoryLabel = category
      ? ` - ${category.charAt(0).toUpperCase() + category.slice(1)} Templates`
      : '';

    // Separate starred templates
    const starredTemplates = templates.filter(entity =>
      starredEntityRefs.has(getEntityRef(entity)),
    );
    const nonStarredTemplates = templates.filter(
      entity => !starredEntityRefs.has(getEntityRef(entity)),
    );

    const result: GroupedTemplate[] = [];

    // Add "Starred Templates" group if there are any starred templates
    if (starredTemplates.length > 0) {
      result.push({
        title: 'Starred Templates',
        description: undefined,
        templates: starredTemplates,
      });
    }

    if (!groups || groups.length === 0) {
      if (nonStarredTemplates.length > 0) {
        result.push({
          title: 'Choose from Templates',
          description: undefined,
          templates: nonStarredTemplates,
        });
      }
      return result;
    }

    const grouped = groups.map(group => ({
      title: `${group.title}${categoryLabel}`,
      description: group.description,
      templates: nonStarredTemplates.filter(group.filter),
    }));

    const hasGroupedTemplates = grouped.some(
      group => group.templates.length > 0,
    );

    const unmatchedTemplates = findUnmatchedTemplates(
      nonStarredTemplates,
      grouped,
    );

    if (unmatchedTemplates.length > 0) {
      grouped.push({
        title: createFallbackGroupTitle(hasGroupedTemplates, category),
        description: undefined,
        templates: unmatchedTemplates,
      });
    }

    return [...result, ...grouped];
  }, [templates, starredEntityRefs, groups, category]);
};

const TemplateGroupSection = ({ group }: { group: GroupedTemplate }) => {
  if (group.templates.length === 0) {
    return null;
  }

  return (
    <Flex direction="column" gap="2" mb="10">
      <Text variant="title-x-small">{group.title}</Text>
      {group.description && (
        <Text variant="body-medium">{group.description}</Text>
      )}
      <Grid.Root columns={{ initial: '2', xs: '2', lg: '3', xl: '4' }} gap="4">
        {group.templates.map(entity => (
          <Grid.Item key={entity.metadata.name}>
            <CustomTemplateCard template={entity as TemplateEntityV1beta3} />
          </Grid.Item>
        ))}
      </Grid.Root>
    </Flex>
  );
};

export const ScaffolderPage = ({ category, groups }: ScaffolderPageProps) => {
  const catalogApi = useApi(catalogApiRef);
  const categories = useTemplateCategories(catalogApi);
  const { starredEntities } = useStarredEntities();

  const [searchText, setSearchText] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(
    new Set(),
  );

  const starredEntityRefs = useMemo(() => {
    return new Set(
      Array.from(starredEntities).map((ref: string) => ref.toLowerCase()),
    );
  }, [starredEntities]);

  const { value, loading, error } = useAsync(async () => {
    const res = await catalogApi.getEntities({
      filter: { kind: 'Template' },
    });

    return res.items;
  }, [catalogApi]);

  const templates = useMemo(() => {
    const entities = value ?? [];
    const categoryFiltered = filterTemplatesByCategory(entities, category);
    const searchFiltered = filterTemplatesBySearch(
      categoryFiltered,
      searchText,
    );

    // Filter by selected categories (checkbox filters)
    const selectedCategoryFiltered =
      selectedCategories.size > 0
        ? searchFiltered.filter((entity: Entity) => {
            const entityType = getTemplateType(entity);
            return (
              typeof entityType === 'string' &&
              selectedCategories.has(entityType)
            );
          })
        : searchFiltered;

    return sortTemplatesByStarred(selectedCategoryFiltered, starredEntityRefs);
  }, [value, category, searchText, starredEntityRefs, selectedCategories]);

  const groupedTemplates = useTemplateGroups(
    templates,
    starredEntityRefs,
    groups,
    category,
  );

  const totalTemplatesCount = groupedTemplates.reduce(
    (sum, group) => sum + group.templates.length,
    0,
  );

  const handleCategoryToggle = (keys: Set<React.Key>) => {
    setSelectedCategories(new Set(Array.from(keys).map(String)));
  };
  const onSearchTextChange = (searchValue: string) => {
    setSearchText(searchValue);
  };

  if (loading) {
    return (
      <Container>
        <Progress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Text color="warning">Could not fetch templates: {String(error)}</Text>
      </Container>
    );
  }

  const templatePlural = totalTemplatesCount === 1 ? '' : 's';
  const searchDescription = searchText
    ? `Filtering results: ${totalTemplatesCount} template${templatePlural} found matching "${searchText}"`
    : 'Search templates by title, description, or owner';

  return (
    <Container>
      <Flex direction="column" gap="2">
        <Flex direction="row" gap="2" p="1" mb="5">
          <SearchField
            label=" "
            description={searchDescription}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            onChange={onSearchTextChange}
            onSubmit={() => undefined}
            value={searchText}
            size="medium"
            aria-label="Search Templates"
          />

          {categories.length > 0 && (
            <Flex direction="column" style={{ alignSelf: 'end' }} pb="1">
              <ToggleButtonGroup
                selectionMode="multiple"
                onSelectionChange={handleCategoryToggle}
              >
                {categories.length > 0 && (
                  <>
                    {categories.map(categoryName => (
                      <ToggleButton key={categoryName} id={categoryName}>
                        {categoryName.charAt(0).toUpperCase() +
                          categoryName.slice(1)}
                      </ToggleButton>
                    ))}
                  </>
                )}
              </ToggleButtonGroup>
            </Flex>
          )}
        </Flex>

        {groupedTemplates.map(group => (
          <TemplateGroupSection key={group.title} group={group} />
        ))}
      </Flex>
    </Container>
  );
};
